import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { apiContainer } from '@vlabs/api-bindings';
import type { VLAccessClient } from '@vlabs/api-bindings';
import i18n from 'i18next';

import type { TUiStreamGroup } from '../types';

const REFETCH_DEFAULT_TIME = 5 * 60 * 1000;

type TVLAccessSourceName = string;
interface TBuildUiVLAccessSourcesGroupArgs {
  names?: TVLAccessSourceName[];
}

function buildUiVLAccessSourcesGroup({ names }: TBuildUiVLAccessSourcesGroupArgs): TUiStreamGroup | null {
  const filtered = (names ?? []).filter(Boolean);

  if (filtered.length === 0) return null;

  return {
    id: '__vl_access__',
    label: i18n.t('sources.luna access source title'),
    defaultCollapsed: false,
    items: filtered.map((name) => ({
      id: `vlaccess:${name}`,
      value: name,
      label: name,
    })),
  };
}

interface TUseVLAccessSourcesResult {
  uiVlAccessGroup: TUiStreamGroup | null;
}

export function useVLAccessSources(): TUseVLAccessSourcesResult {
  const vlAccessClient = apiContainer.vlAccessClient as VLAccessClient;

  const sourcesQuery = useQuery<TVLAccessSourceName[]>({
    queryKey: ['vl-access', 'sources'],
    queryFn: async () => {
      const res = await vlAccessClient.sources.getAll();
      return res?.data ?? [];
    },
    staleTime: REFETCH_DEFAULT_TIME,
  });

  const uiVlAccessGroup = useMemo(() => buildUiVLAccessSourcesGroup({ names: sourcesQuery.data }), [sourcesQuery.data]);

  return { uiVlAccessGroup };
}
